import { ALL_WORDS, FINANCE_CONTEXT_WORDS, URGENCY_MODIFIERS, WordSeverity } from './spamWordList';
import {
  PatternHit,
  assessCapsRuns,
  assessEmojis,
  assessExclamations,
  assessExtremePercentagePromises,
  assessImageOnlyBody,
  assessLinkDensity,
  assessSubjectLength,
  htmlToPlainText,
} from './patternRules';

export interface FlaggedPhrase {
  text: string;
  severity: WordSeverity;
  location: 'subject' | 'preheader' | 'body';
  reason: string;
  suggestion: string;
  start: number;
  end: number;
}

export interface Layer1Result {
  score: number;
  flaggedPhrases: FlaggedPhrase[];
  patternHits: PatternHit[];
  redCount: number;
  orangeCount: number;
  yellowCount: number;
  breakdown: {
    wordPenalty: number;
    patternPenalty: number;
    subjectLengthPenalty: number;
    linkDensityPenalty: number;
    imageOnlyPenalty: number;
  };
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const CONTEXT_WINDOW = 60;

function hasFinanceContext(text: string, start: number, end: number): boolean {
  const windowStart = Math.max(0, start - CONTEXT_WINDOW);
  const windowEnd = Math.min(text.length, end + CONTEXT_WINDOW);
  const window = text.slice(windowStart, windowEnd).toLowerCase();
  return FINANCE_CONTEXT_WORDS.some((w) => window.includes(w));
}

function hasUrgencyModifier(text: string, start: number, end: number): boolean {
  const windowStart = Math.max(0, start - CONTEXT_WINDOW);
  const windowEnd = Math.min(text.length, end + CONTEXT_WINDOW);
  const window = text.slice(windowStart, windowEnd).toLowerCase();
  return URGENCY_MODIFIERS.some((m) => new RegExp(`\\b${m}\\b`, 'i').test(window));
}

function matchWordsInText(
  text: string,
  location: 'subject' | 'preheader' | 'body',
): FlaggedPhrase[] {
  if (!text) return [];

  const candidates = ALL_WORDS.filter((w) => {
    if (!w.location || w.location === 'any') return true;
    if (location === 'subject') return w.location === 'subject';
    return w.location === 'body';
  });

  const sorted = [...candidates].sort((a, b) => b.phrase.length - a.phrase.length);

  const consumed = new Array(text.length).fill(false);
  const results: FlaggedPhrase[] = [];

  for (const entry of sorted) {
    const pattern = new RegExp(
      `(?<![\\p{L}\\d])${escapeRegex(entry.phrase)}(?![\\p{L}\\d])`,
      'giu',
    );
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(text)) !== null) {
      const start = match.index;
      const end = start + match[0].length;

      let overlaps = false;
      for (let i = start; i < end; i++) {
        if (consumed[i]) {
          overlaps = true;
          break;
        }
      }
      if (overlaps) continue;

      if (entry.contextRule === 'finance_downgrade' && hasFinanceContext(text, start, end)) {
        continue;
      }

      if (
        entry.contextRule === 'requires_urgency_modifier' &&
        !hasUrgencyModifier(text, start, end)
      ) {
        continue;
      }

      for (let i = start; i < end; i++) consumed[i] = true;

      results.push({
        text: match[0],
        severity: entry.severity,
        location,
        reason: entry.reason,
        suggestion: entry.suggestion,
        start,
        end,
      });
    }
  }

  return results;
}

const SEVERITY_WEIGHTS: Record<WordSeverity, number> = {
  red: 15,
  orange: 8,
  yellow: 3,
};

const YELLOW_TOTAL_CAP = 15;

export function scoreContentLayer1(subject: string, preheader: string, body: string): Layer1Result {
  const bodyPlain = htmlToPlainText(body || '');

  const subjectFlags = matchWordsInText(subject || '', 'subject');
  const preheaderFlags = matchWordsInText(preheader || '', 'preheader');
  const bodyFlags = matchWordsInText(bodyPlain, 'body');

  const flaggedPhrases = [...subjectFlags, ...preheaderFlags, ...bodyFlags];

  const redCount = flaggedPhrases.filter((f) => f.severity === 'red').length;
  const orangeCount = flaggedPhrases.filter((f) => f.severity === 'orange').length;
  const yellowCount = flaggedPhrases.filter((f) => f.severity === 'yellow').length;

  const redPenalty = redCount * SEVERITY_WEIGHTS.red;
  const orangePenalty = orangeCount * SEVERITY_WEIGHTS.orange;
  const yellowPenaltyRaw = yellowCount * SEVERITY_WEIGHTS.yellow;
  const yellowPenalty = Math.min(yellowPenaltyRaw, YELLOW_TOTAL_CAP);

  const wordPenalty = redPenalty + orangePenalty + yellowPenalty;

  const patternHits: PatternHit[] = [];
  patternHits.push(...assessSubjectLength(subject || ''));
  patternHits.push(...assessExclamations(subject || '', 'subject'));
  patternHits.push(...assessExclamations(preheader || '', 'preheader'));
  patternHits.push(...assessExclamations(bodyPlain, 'body'));
  patternHits.push(...assessCapsRuns(subject || '', 'subject'));
  patternHits.push(...assessCapsRuns(preheader || '', 'preheader'));
  patternHits.push(...assessCapsRuns(bodyPlain, 'body'));
  patternHits.push(...assessEmojis(subject || ''));
  patternHits.push(...assessExtremePercentagePromises(subject || '', 'subject'));
  patternHits.push(...assessExtremePercentagePromises(preheader || '', 'preheader'));
  patternHits.push(...assessExtremePercentagePromises(bodyPlain, 'body'));
  patternHits.push(...assessLinkDensity(body || ''));
  patternHits.push(...assessImageOnlyBody(body || ''));

  const subjectLengthPenalty = patternHits
    .filter((h) => h.type === 'subject_too_short' || h.type === 'subject_too_long')
    .reduce((sum, h) => sum + h.penalty, 0);

  const linkDensityPenalty = patternHits
    .filter((h) => h.type === 'high_link_density')
    .reduce((sum, h) => sum + h.penalty, 0);

  const imageOnlyPenalty = patternHits
    .filter((h) => h.type === 'image_only_body')
    .reduce((sum, h) => sum + h.penalty, 0);

  const otherPatternPenalty = patternHits
    .filter(
      (h) =>
        h.type !== 'subject_too_short' &&
        h.type !== 'subject_too_long' &&
        h.type !== 'high_link_density' &&
        h.type !== 'image_only_body',
    )
    .reduce((sum, h) => sum + h.penalty, 0);

  const patternPenalty =
    otherPatternPenalty + subjectLengthPenalty + linkDensityPenalty + imageOnlyPenalty;

  const total = wordPenalty + patternPenalty;
  const score = Math.max(0, Math.min(100, 100 - total));

  return {
    score,
    flaggedPhrases,
    patternHits,
    redCount,
    orangeCount,
    yellowCount,
    breakdown: {
      wordPenalty,
      patternPenalty: otherPatternPenalty,
      subjectLengthPenalty,
      linkDensityPenalty,
      imageOnlyPenalty,
    },
  };
}
